import { ChartConfig, ChartContainer } from "design-system/molecules/Chart"
import { cn } from "design-system/utils"
import { formatPercent } from "lopermut"
import { getSolveRateColor } from "modules/shared/lib/sentiment"
import { useTranslation } from "react-i18next"
import { Cell, Label, Pie, PieChart } from "recharts"

const chartConfig = {} satisfies ChartConfig

type Props = {
  solveRate: number | null | undefined
  potentialSolveRate?: number | null
  mini?: boolean
  labelKey?: string
  getColor?: (rate: number) => string
  potentialColor?: string
}

export function SolveRateScore({
  solveRate,
  potentialSolveRate,
  mini,
  labelKey = "shared.rates.solveRate",
  getColor = getSolveRateColor,
  potentialColor = "var(--support-secondary)",
}: Props) {
  const { t } = useTranslation()

  // Treat 0 as valid, only null/undefined means no data
  const noData = solveRate == null
  const rate = Math.max(0, Math.min(1, solveRate ?? 0))
  const potentialRate = Math.max(rate, Math.min(1, potentialSolveRate ?? 0))
  const hasPotential = potentialSolveRate != null && potentialRate > rate + 0.05
  const isHighPotential = hasPotential && potentialRate - rate >= 0.2
  const chartSolveRate = formatPercent({ amount: rate }, true)

  const chartData = hasPotential
    ? [{ value: rate }, { value: potentialRate - rate }, { value: 1 - potentialRate }]
    : [{ value: rate }, { value: 1 - rate }]

  const potentialStartAngle = hasPotential ? 90 + rate * -360 : undefined
  const potentialEndAngle = hasPotential ? 90 + potentialRate * -360 : undefined

  return (
    <ChartContainer
      config={chartConfig}
      className={cn(
        "aspect-square",
        mini ? "h-[14px]" : "h-[100px]",
        noData && "pointer-events-none grayscale opacity-25 dark:opacity-5",
      )}
    >
      <PieChart>
        <Pie
          data={chartData}
          dataKey="value"
          startAngle={90}
          endAngle={-270}
          innerRadius={mini ? 5 : 40}
          outerRadius={mini ? 7 : 50}
          cornerRadius={0}
          animationDuration={0}
        >
          <Cell fill={`var(--${getColor(rate)})`} />
          {hasPotential && <Cell fill="transparent" />}
          <Cell fill="var(--muted)" />
          {!mini && (
            <Label
              content={({ viewBox }) => {
                if (viewBox && "cx" in viewBox && "cy" in viewBox) {
                  return (
                    <text
                      x={viewBox.cx}
                      y={viewBox.cy}
                      textAnchor="middle"
                      dominantBaseline="middle"
                    >
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy ?? 0) - 6}
                        className="fill-muted-foreground"
                      >
                        {chartSolveRate}
                      </tspan>
                      <tspan
                        x={viewBox.cx}
                        y={(viewBox.cy ?? 0) + 10}
                        className="text-[10px] fill-muted-foreground"
                      >
                        {t(labelKey)}
                      </tspan>
                    </text>
                  )
                }
              }}
            />
          )}
        </Pie>

        {hasPotential && potentialStartAngle != null && potentialEndAngle != null && (
          <Pie
            data={[{ value: 1 }]}
            dataKey="value"
            startAngle={potentialStartAngle}
            endAngle={potentialEndAngle}
            innerRadius={mini ? 5.5 : 40}
            outerRadius={mini ? 7.5 : 50}
            cornerRadius={0}
            animationDuration={0}
          >
            <Cell
              fill="none"
              stroke={potentialColor}
              strokeWidth={1.5}
              strokeOpacity={isHighPotential ? 1 : 0.5}
            />
          </Pie>
        )}
      </PieChart>
    </ChartContainer>
  )
}
